import React, { useEffect, useState } from 'react';
import {
  AnimatePresence,
  cubicBezier,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { NavigationButtons } from '@features/onboarding/components/shared/ui/NavigationButtons';
import { BottomNavigationButtons } from '@features/onboarding/components/shared/ui/BottomNavigationButtons';
import { RightArrowIcon } from '@components/shared/icon/RightArrowIcon';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useUnderReviewStore } from 'src/store/under-review';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useAIStore } from 'src/store/ai';
import { useCompareStore } from 'src/store/compare';
import { applicationService } from 'src/services/application';

import { cn } from '@utils/index';
import {
  // FormStepID,
  FormStepSections,
} from '@features/onboarding/enums/formSections.enum';
// import { StepNavigator } from '@features/stepNavigator/stepNavigator';
import { ACCEPTANCE_WARNING_LS_KEY } from 'src/constants';

const StepBlockContext = React.createContext(false);
export const useStepBlockContext = () => React.useContext(StepBlockContext);

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const formatText = (text: string): string => {
  if (!text) return '';
  let formatted = text.replace(/\n/g, '<br />');
  formatted = formatted.replace(/([.!?])([A-ZА-ЯЁ])/g, '$1 $2');
  return formatted;
};

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};

// Вспомогательная функция для очистки и закрытия compareStore
const clearAndCloseCompareStore = () => {
  const { clearSelectedPlans, close, setCompareViewCategory } =
    useCompareStore.getState();
  clearSelectedPlans();
  close();
  setCompareViewCategory(null);
};

const handlePrevClick = (
  onPrev: ((e?: React.BaseSyntheticEvent) => void) | undefined,
  handlePreviousStep: () => void,
  e?: React.BaseSyntheticEvent
) => {
  clearAndCloseCompareStore();
  if (onPrev) {
    onPrev(e);
  } else {
    handlePreviousStep();
  }
};

export const OnboardingFormStepLayout = ({
  children,
  handleFormSubmit,
  onPrev,
  onChange,
  isFormValid,
  idStep: propIdStep,
  title: propTitle,
  description: propDescription,
  subscription: propSubscription,
  hideNextButton,
  isSubmitText,
  onlyImage,
  headerClassName,
  isLoading: propIsLoading,
  backgroundClassName,
  isAnimationScreen,
  nextButtonLabel,
  headerAnimationKey,
  contentAnimationKey,
  disabled = false, // TODO надо будет проверить и уточнить
}: {
  children: React.ReactNode | React.ReactNode[];
  handleFormSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onPrev?: (e?: React.BaseSyntheticEvent) => void;
  onChange?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isFormValid?: boolean;
  idStep?: string;
  title?: string;
  description?: string;
  subscription?: string;
  hideNextButton?: boolean;
  isSubmitText?: boolean;
  onlyImage?: boolean;
  headerClassName?: string;
  isLoading?: boolean;
  backgroundClassName?: string;
  isAnimationScreen?: boolean;
  nextButtonLabel?: string;
  headerAnimationKey?: string;
  contentAnimationKey?: string;
  disabled?: boolean;
}) => {
  const {
    currentFormStep,
    formData,
    isLocalLoading,
    handleNextStep,
    handlePreviousStep,
    setIsLocalLoading,
  } = useFormControls();
  if (!currentFormStep) return null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentFormStep?.id]);
  const headerVariants = usePageTransitionVariants();
  const contentVariants = usePageTransitionVariants(0.1);

  const formSteps = useFormSteps();
  const { number } = currentFormStep;
  const idStep = propIdStep ?? currentFormStep.id;
  const title = propTitle ?? currentFormStep.title;
  const description = propDescription ?? currentFormStep.description;
  const subscription = propSubscription ?? currentFormStep.subscription;

  useEffect(() => {
    if (idStep) {
      useAIStore.getState().setCurrentPage(idStep);
    }
  }, [idStep]);

  const isLoading = propIsLoading || isLocalLoading;

  const {
    createApplication,
    getCurrentApplication,
    updateApplicationById,
    currentApplicationId,
    applications,
    activeSubscriptionPlans,
    status,
    signedAt,
    fetchActiveSubscriptionPlans,
    phqUnderReviewAt,
  } = useApplicationStore();
  const { isUnderReview, checkUnderReviewStatus } = useUnderReviewStore();
  const { isApplicationAccepted, setModalOpen: setAcceptanceWarningModalOpen } =
    useAcceptanceChangeStore();
  const { data } = useOnboardingStore.getState();

  const plans = useInsuranceStore((state) => state.plans);

  // State to track if user has acceptedTier (tier determined)
  const [hasAcceptedTier, setHasAcceptedTier] = useState<boolean>(false);

  // State to track if user has signed contract (AfterSignIn modal)
  const [isAfterSignInModalOpen, setIsAfterSignInModalOpen] =
    useState<boolean>(false);

  const isHealthOrDependentsSection =
    currentFormStep?.sectionName === FormStepSections.HealthQuestion ||
    currentFormStep?.sectionName === FormStepSections.Dependents;
  const allowMedicalOrDentalInteraction =
    !signedAt &&
    (currentFormStep?.sectionName === FormStepSections.Medical ||
      currentFormStep?.sectionName === FormStepSections.Dental) &&
    hasAcceptedTier &&
    !isAfterSignInModalOpen;
  const allowFinishInteraction =
    currentFormStep?.sectionName === FormStepSections.Finish &&
    !isAfterSignInModalOpen;
  const baseBlocked =
    disabled ||
    isUnderReview ||
    isApplicationAccepted ||
    isAfterSignInModalOpen ||
    (hasAcceptedTier && isHealthOrDependentsSection);
  const isBlockedTmp =
    baseBlocked &&
    !allowMedicalOrDentalInteraction &&
    !allowFinishInteraction &&
    idStep !== 'coverage_start_date';

  const isBlocked = isBlockedTmp || phqUnderReviewAt;

  // Handler for blocked content click
  const handleBlockedContentClick = () => {
    console.log(
      'handleBlockedContentClick',
      disabled,
      isUnderReview,
      isApplicationAccepted,
      isAfterSignInModalOpen,
      signedAt,
      currentFormStep?.sectionName,
      hasAcceptedTier,
      isAfterSignInModalOpen
    );

    console.log('phqUnderReviewAt', phqUnderReviewAt);

    const blockModal = phqUnderReviewAt

    // Open AcceptanceWarningModal if blocked and not under review
    if (isBlocked && !blockModal) {
      localStorage.removeItem(ACCEPTANCE_WARNING_LS_KEY);
      setAcceptanceWarningModalOpen(true);
    }
  };

  useEffect(() => {
    fetchActiveSubscriptionPlans();
  }, [fetchActiveSubscriptionPlans]);

  useEffect(() => {
    getCurrentApplication();
  }, []);

  // Check if user has acceptedTier (tier determined by AI/human)
  useEffect(() => {
    const checkAcceptedTier = async () => {
      try {
        checkUnderReviewStatus();
        const applicationState = await applicationService.applicationState();
        setHasAcceptedTier(!!applicationState.acceptedTier);
      } catch (error) {
        console.error('Failed to check acceptedTier:', error);
        setHasAcceptedTier(false);
      }
    };

    checkAcceptedTier();
  }, []);

  // Check if user has signed contract (show AfterSignIn modal)
  useEffect(() => {
    const checkContractSignature = () => {
      if (applications.length > 0 && applications[0]) {
        const application = applications[0] as any; // Type assertion since signature might not be in interface
        const hasSignedContract =
          application.signature?.signedAt !== null &&
          application.signature?.signedAt !== undefined;
        setIsAfterSignInModalOpen(hasSignedContract);
        console.log('Contract signature check:', {
          hasSignedContract,
          signedAt: application.signature?.signedAt,
        });
      }
    };

    checkContractSignature();
  }, [applications]);

  useEffect(() => {
    const _data = transformApplicationToOnboardingData(applications[0], plans);
    console.log('applications', applications.length); // HACK: вызываем тем самым обновляя
    if (applications.length > 0) {
      console.log('applications', applications[0]);
    }
    if (JSON.stringify(data) === '{}') {
      useOnboardingStore.getState().setData(_data);
      console.log('updated', data); // HACK: вызываем тем самым обновляя
    }
  }, [applications]);
  console.log('idStep', idStep, number);
  const handleNextClick = async (e?: React.BaseSyntheticEvent) => {
    clearAndCloseCompareStore();
    if (handleFormSubmit) {
      await handleFormSubmit(e);
    } else {
      const data = useOnboardingStore.getState().data;
      useOnboardingStore.getState().setData(data);
    }
    const updatedFormData = useOnboardingStore.getState().data;
    if (isFormValid) {
      try {
        setIsLocalLoading(true);

        const applicationData = transformApplicationData(
          updatedFormData,
          plans
        );
        console.log(
          'status-onboarding',
          status,
          currentApplicationId,
          applicationData,
          updatedFormData
        );
        if (status === 'created' && currentApplicationId) {
          await updateApplicationById(currentApplicationId, applicationData);
        } else {
          await createApplication(applicationData);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLocalLoading(false);
      }
    }
  };

  const Header = (
    <motion.header
      key={`header_${headerAnimationKey || currentFormStep.id}`}
      variants={headerVariants}
      initial="initial"
      animate="animate"
      // exit="exit"
      className={cn(
        'w-full relative rounded-3xl p-5 md:pt-10 md:pb-8 md:px-7.5 bg-primary max-md:min-w-fit flex flex-col gap-4 max-xl:min-h-102.5 max-s:min-h-77',
        'h-full justify-between text-white 2xl:max-w-[34.83%]',
        onlyImage && 'max-s:min-h-102.5',
        headerClassName
        // onlyImage && 'max-lg:mb-6'
      )}
    >
      <div className="bg-primary absolute top-25 -right-1 w-8 h-8 pointer-events-none select-none max-2xl:hidden rotate-45" />
      <section
        id="add-header-left-text"
        className="flex flex-col gap-6 relative overflow-y-auto max-md:gap-2 h-full lg:pr-1.5"
      >
        <p className="header-text text-[19px] sticky top-0 bg-primary leading-6 tracking-[30%] uppercase align-middle max-md:text-[14px]">
          step {formSteps.length >= number ? number : formSteps.length} /{' '}
          {formSteps.length}
        </p>
        <h2 className="font-bold text-2xl leading-7 lg:leading-10 max-md:tracking-[1px] lg:text-4xl 3xl:text-[40px]">
          {title}
        </h2>
        <p
          className="text-base max-md:leading-normal max-md:mt-2.5 max-lg:text-base"
          dangerouslySetInnerHTML={{
            __html: formatText(description || ''),
          }}
        />
        {number === 3 && subscription && (
          <p
            className="text-base max-md:leading-normal max-md:mt-2.5 max-lg:text-base"
            dangerouslySetInnerHTML={{
              __html: formatText(subscription),
            }}
          />
        )}
      </section>
      {number === 1 ? (
        <div id="app-header-left-nav" className="max-2xl:hidden mt-auto flex">
          <button
            className="border border-white whitespace-nowrap relative mt-2 bg-white items-center font-bold uppercase w-full flex justify-center rounded-full px-15 py-1.5 text-[19px] cursor-pointer text-primary md:py-3.5"
            onClick={() => handleNextStep(formData)}
          >
            <span>Let's Get Started!</span>
            <RightArrowIcon
              color="var(--color-primary)"
              className="absolute right-5"
            />
          </button>
        </div>
      ) : (
        <div id="app-header-left-nav" className="max-2xl:hidden flex">
          <NavigationButtons
            handleFormSubmit={handleNextClick}
            onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
            onChange={onChange}
            isFormValid={isFormValid}
            hideNextButton={hideNextButton}
            isLoading={isLoading}
            isSubmitText={isSubmitText}
            nextButtonLabel={nextButtonLabel}
          />
        </div>
      )}
    </motion.header>
  );

  return (
    <StepBlockContext.Provider value={isBlocked}>
      <div
        className={cn(
          'flex flex-col gap-4 justify-center pr-1 relative pb-0 max-xl:pb-0 h-full',
          onlyImage ? '2xl:flex-row-reverse' : '2xl:flex-row'
        )}
      >
        {/* <StepNavigator
        onNext={handleNextClick}
        onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
        isFormValid={isFormValid}
      /> */}
        {onlyImage ? (
          <>
            <motion.div
              id="childrenBlock"
              key={
                headerAnimationKey || contentAnimationKey || currentFormStep.id
              }
              variants={contentVariants}
              initial="initial"
              animate="animate"
              // exit="exit"
              className="bg-card w-full rounded-2xl"
            >
              {children}
            </motion.div>
            <AnimatePresence mode="wait">{Header}</AnimatePresence>
          </>
        ) : (
          <>
            <AnimatePresence mode="wait">{Header}</AnimatePresence>
            <motion.div
              key={
                headerAnimationKey || contentAnimationKey || currentFormStep.id
              }
              variants={contentVariants}
              initial="initial"
              animate="animate"
              // exit="exit"
              className={cn(
                'w-full xl:pr-4 xl:pt-2.5 xl:pb-6 box-border rounded-2xl h-full',
                backgroundClassName ?? 'bg-card'
              )}
            >
              <div
                id="childrenBlock"
                className={cn(
                  'scrollbar-gutter-stable',
                  'w-full flex flex-col pt-5 xl:pt-2.5 px-5 md:px-7.5 xl:pr-3.5 pb-8 xl:pb-2 overflow-auto 2xl:max-h-full 2xl:h-full max-2xl:relative',
                  isAnimationScreen
                    ? 'items-center justify-center'
                    : 'justify-between',
                  (title === 'Time to talk about medications.' ||
                    title === 'First up: some quick health questions') &&
                    'bg-transparent pb-0 max-md:px-3 md:bg-card rounded-2xl'
                )}
                onClick={handleBlockedContentClick}
              >
                <div
                  className={cn(
                    'h-full',
                    // Block PHQ and Coverage sections if tier is already accepted
                    // Allow interaction on HealthPlans and Dental steps if user has acceptedTier
                    // Also allow interaction on Finish section
                    // Block all interactions if AfterSignIn modal is open (contract signed)
                    isBlocked ? 'pointer-events-none' : ''
                  )}
                >
                  {children}
                </div>
              </div>
            </motion.div>
          </>
        )}
        <BottomNavigationButtons
          handleFormSubmit={handleNextClick}
          onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
          onChange={onChange}
          isFormValid={isFormValid}
          isSubmitText={isSubmitText}
          hideNextButton={hideNextButton}
          nextButtonLabel={nextButtonLabel}
        />
      </div>
    </StepBlockContext.Provider>
  );
};
